/*
 * <chat-clearance> keeps the Shopify Inbox chat bubble off whatever it wraps.
 * The footer wraps its bottom row in it (legal links, credit, payment marks).
 *
 * The bubble (<shopify-chat>, app-owned, fixed 20px from the bottom-right
 * corner) sat on the "Engineered to Convert by 27Street" credit and the last
 * payment marks once a visitor reached the end of the page. While the row is
 * near the viewport, this element puts `chat-clearance-active` on <body> and
 * the lift in px on --chat-clearance-offset. section-site-footer.css feeds
 * that into the widget's own position setting, --shopify-chat-activator-offset.
 * The widget registers that property as a <length>, so the lift animates
 * like any other length. It moves the bubble, not the widget's full-screen
 * host box; on desktop the chat panel opens from wherever the bubble sits.
 *
 * The lift is measured for the end of the page, where the row sits highest on
 * screen. Anywhere else in view the row is lower, so the bubble clears it
 * there too, and the lift stays put while the visitor scrolls. The bubble
 * only lifts when the spot it would land on is free of footer content;
 * otherwise it stays where it is.
 */
if (!customElements.get('chat-clearance')) {
  customElements.define(
    'chat-clearance',
    class ChatClearance extends HTMLElement {
      // Air between the lifted bubble and the top of the row.
      static GAP = 16;
      // The bubble starts its hop while the row is still this far below the
      // fold, so it is up before the row reaches it.
      static LEAD = 120;
      // Horizontal slack: an item this close to the bubble's edge counts as
      // under it.
      static SLACK = 8;

      connectedCallback() {
        this.onIntersect ||= ([entry]) => {
          this.inView = entry.isIntersecting;
          this.update();
        };
        this.onResize ||= () => this.update();
        this.observer = new IntersectionObserver(this.onIntersect, {
          rootMargin: `0px 0px ${ChatClearance.LEAD}px 0px`,
        });
        this.observer.observe(this);
        // Wrapping changes the row's height: new payment marks, a narrower
        // window, a late web font.
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
        window.addEventListener('resize', this.onResize, { passive: true });
        // The widget loads after the theme. Measure its real bubble once it
        // exists instead of the stock bottom-right guess.
        customElements.whenDefined('shopify-chat').then(() => requestAnimationFrame(this.onResize));
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.onResize);
        this.inView = false;
        this.update();
      }

      update() {
        const offset = this.inView ? this.measure() : 0;
        if (offset) document.body.style.setProperty('--chat-clearance-offset', `${offset}px`);
        document.body.classList.toggle('chat-clearance-active', Boolean(offset));
      }

      // The widget's offset (px) that puts the bubble's bottom GAP above the
      // highest item it would cover, or 0 when it covers none or has nowhere
      // free to go.
      measure() {
        const chat = document.querySelector('shopify-chat');
        const bubble = chat?.shadowRoot?.querySelector('[part~="activator"]');
        // Without a rendered bubble, assume the stock one: bottom-right,
        // 20px in, up to 120px wide.
        let left = window.innerWidth - 140;
        let right = window.innerWidth - 20;
        if (bubble?.offsetWidth) {
          // The widget pops the bubble in with `scale` around its centre, so
          // the box on screen can be smaller than where it settles. Take the
          // centre from the screen and the width from layout.
          const { left: boxLeft, right: boxRight } = bubble.getBoundingClientRect();
          const centre = (boxLeft + boxRight) / 2;
          left = centre - bubble.offsetWidth / 2;
          right = centre + bubble.offsetWidth / 2;
        }
        left -= ChatClearance.SLACK;
        right += ChatClearance.SLACK;

        // Distance from the end of the document to an item's top is where that
        // item sits above the viewport's bottom edge at the end of the page.
        // The root's scrollHeight is never below the viewport, so short pages
        // come out right too.
        const end = document.documentElement.scrollHeight;
        let reach = 0;
        for (const item of this.items()) {
          const rect = item.getBoundingClientRect();
          if (!rect.width || rect.right < left || rect.left > right) continue;
          reach = Math.max(reach, end - (rect.top + window.scrollY));
        }
        if (!reach) return 0;

        // Anything that already lifts the whole widget (the configurator's
        // sticky bar sets its `bottom`) also pads the page by the same amount,
        // so take it off rather than count it twice.
        const widgetLift = chat ? parseFloat(getComputedStyle(chat).bottom) || 0 : 0;
        const rootStyle = getComputedStyle(document.documentElement);
        const stock = parseFloat(rootStyle.getPropertyValue('--shopify-chat-activator-offset')) || 20;
        const offset = Math.ceil(reach + ChatClearance.GAP - widgetLift);
        // Already clear at its stock height: no lift, and no hop in place.
        if (offset <= stock) return 0;

        // Make room only where there is room. On phones the stacked footer
        // puts the store hours right above the row, so the bubble would just
        // cover something else: it stays put there (the credit, left-aligned
        // on phones, is never under it).
        const low = offset + widgetLift;
        const high = low + (bubble?.offsetHeight || 60);
        return this.isFree(left, right, low, high, end) ? offset : 0;
      }

      // True when no text, image or control of the surrounding footer lies in
      // the box [left, right] x [low, high], with low and high in px above
      // the end of the page (the same measure as `reach`).
      isFree(left, right, low, high, end) {
        const scope = this.closest('footer') ?? this.parentElement;
        const range = document.createRange();
        const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
          let boxes;
          if (node.nodeType === Node.TEXT_NODE) {
            // Screen-reader-only text is clipped, but its line boxes still
            // report where it would have run.
            if (!node.data.trim() || node.parentElement.closest('.visually-hidden')) continue;
            range.selectNodeContents(node);
            boxes = range.getClientRects();
          } else if (node.matches('img, svg, input, textarea, select, button, video, iframe')) {
            // Media hidden from assistive tech is decoration: the collection
            // banner behind the whole footer is one.
            if (node.matches('img, video') && node.closest('[aria-hidden="true"]')) continue;
            boxes = [node.getBoundingClientRect()];
          } else {
            continue;
          }
          for (const box of boxes) {
            if (box.width <= 1 || box.height <= 1 || box.right < left || box.left > right) continue;
            const top = end - (box.top + window.scrollY);
            const bottom = end - (box.bottom + window.scrollY);
            if (bottom < high && top > low) return false;
          }
        }
        return true;
      }

      // The row's children, with lists measured item by item: a wrapped list's
      // box spans the full row even when its items stop well short of the
      // bubble.
      items() {
        return [...this.children].flatMap((child) => {
          const listItems = child.querySelectorAll('li');
          return listItems.length ? [...listItems] : [child];
        });
      }
    }
  );
}
